import { useContext } from "react";
import { AuthContext } from "../../auth/AuthContext";
import { usePortalData } from "../context/PortalDataContext";

/**
 * Derives all home page card data from the shared PortalDataContext.
 * No API calls here — the layout already fetched everything once.
 */
export default function useHomeData() {
  const { user } = useContext(AuthContext);

  const {
    activePrescriptionCount,
    pendingRefillCount,
    totalPrescriptionCount,
    prescriptions,
    refillRequests,
    uploads,
    loading,
  } = usePortalData();

  // ── Upcoming refills ─────────────────────────────────────────────────────
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const upcomingRefills = prescriptions
    .filter((rx) => rx.next_refill)
    .map((rx) => {
      const daysLeft      = Math.ceil((new Date(rx.next_refill) - today) / 86400000);
      const firstProduct  = rx.items[0]?.product ?? "Unknown";
      const medicineLabel = rx.items.length === 1
        ? firstProduct
        : `${firstProduct} +${rx.items.length - 1} more`;
      return { id: rx.id, medicine: medicineLabel, daysLeft, doctor: rx.doctor, itemCount: rx.items.length };
    })
    .filter((r) => r.daysLeft <= 30)
    .sort((a, b) => a.daysLeft - b.daysLeft)
    .slice(0, 5);

  // ── Activity feed — merged from all sources ───────────────────────────
  const refillEvents = refillRequests.map((r) => {
    const productLabel = r.items?.length === 1
      ? r.items[0].product
      : `${r.items?.length ?? 0} medicines`;
    const statusMeta = {
      pending:    { type: "amber", verb: "submitted"  },
      processing: { type: "blue",  verb: "processing" },
      processed:  { type: "green", verb: "processed"  },
      rejected:   { type: "red",   verb: "rejected"   },
    };
    const meta = statusMeta[r.status] ?? { type: "blue", verb: "updated" };
    return {
      id:    `refill-${r.id}`,
      title: `Refill ${meta.verb}: ${productLabel}`,
      time:  r.date,
      type:  meta.type,
    };
  });

  const uploadEvents = (uploads || []).map((u) => {
    const statusMeta = {
      pending_review: { type: "amber", label: "uploaded for review" },
      processed:      { type: "green", label: "upload processed"    },
      rejected:       { type: "red",   label: "upload rejected"     },
    };
    const meta = statusMeta[u.status] ?? { type: "blue", label: "uploaded" };
    return {
      id:    `upload-${u.id}`,
      title: `Prescription ${meta.label}: ${u.original_name}`,
      time:  u.created_at,
      type:  meta.type,
    };
  });

  const prescriptionEvents = prescriptions.map((rx) => {
    const firstProduct  = rx.items?.[0]?.product ?? "Prescription";
    const label = rx.items?.length > 1
      ? `${firstProduct} +${rx.items.length - 1} more`
      : firstProduct;
    return {
      id:    `rx-${rx.id}`,
      title: `Prescription added: ${label}`,
      time:  rx.date,
      type:  "blue",
    };
  });

  // Merge, sort by date descending, take top 6
  const activityFeed = [...refillEvents, ...uploadEvents, ...prescriptionEvents]
    .filter(e => e.time)
    .sort((a, b) => new Date(b.time) - new Date(a.time))
    .slice(0, 6)
    .map(e => ({ ...e, time: formatRelativeDate(e.time) }));

  return {
    loading,
    user,
    activePrescriptionCount,
    pendingRefillCount,
    totalPrescriptionCount,
    upcomingRefills,
    activityFeed,
  };
}

// ── Utility ──────────────────────────────────────────────────────────────────
function formatRelativeDate(iso) {
  if (!iso) return "";
  const date     = new Date(iso);
  const today    = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((today - date) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7)  return `${diffDays} days ago`;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}